import { ChangeDetectionStrategy, Component, effect, inject, input, signal, untracked } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { CloudApiService, LocationGeoResponse, ResolvedGeo } from '@/cloud/services/cloud-api.service';
import { formatCoordinates, googleMapsUrl } from '@/cloud/models/geo';
import { TranslationService } from '@/layout/service/translation.service';
import { GeoLocationDialogComponent } from '../geo-location-dialog/geo-location-dialog.component';

/**
 * A location's map position on its Settings tab: what is in effect and where it comes from (set here, or inherited from a
 * parent), with the map dialog to set it here or go back to inheriting. Rooms and control systems below inherit it.
 */
@Component({
    selector: 'app-location-geo',
    standalone: true,
    imports: [ButtonModule, GeoLocationDialogComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        @if (data(); as d) {
            <div class="lgeo">
                <span class="lgeo__icon" [class.lgeo__icon--empty]="!d.effective" aria-hidden="true"><i class="pi pi-map-marker"></i></span>
                <div class="lgeo__body">
                    @if (d.effective; as geo) {
                        <span class="lgeo__value">{{ place(geo) }}</span>
                        <span class="lgeo__source">{{ sourceText(geo) }}</span>
                    } @else {
                        <span class="lgeo__value">{{ t()('locationGeo.notSet') }}</span>
                        <span class="lgeo__source">{{ t()('locationGeo.notSetHint') }}</span>
                    }
                </div>
                @if (d.effective; as geo) {
                    <a class="lgeo__maps" [href]="mapsUrl(geo)" target="_blank" rel="noopener noreferrer">{{ t()('locationGeo.openMaps') }} <i class="pi pi-external-link" aria-hidden="true"></i></a>
                }
            </div>
            @if (canManage()) {
                <p-button class="lgeo__edit" [label]="t()(d.override ? 'locationGeo.change' : 'locationGeo.set')" icon="pi pi-pencil" size="small" [text]="true" (onClick)="dialogVisible.set(true)" />
            }
            <app-geo-location-dialog
                [visible]="dialogVisible()"
                (visibleChange)="dialogVisible.set($event)"
                [locationId]="locationId()"
                [locationName]="locationName()"
                [initial]="d.effective"
                [hasOverride]="!!d.override"
                (locationGeoChange)="changed($event)"
            />
        } @else if (loadFailed()) {
            <p class="lgeo__muted">{{ t()('locationGeo.loadError') }}</p>
        } @else {
            <p class="lgeo__muted">…</p>
        }
    `,
    styles: [
        `
            :host {
                display: flex;
                flex-direction: column;
                gap: 0.5rem;
            }
            .lgeo {
                display: flex;
                align-items: center;
                flex-wrap: wrap;
                gap: 0.75rem;
            }
            .lgeo__icon {
                display: grid;
                place-items: center;
                width: 2.25rem;
                height: 2.25rem;
                border-radius: 0.6rem;
                background: var(--app-status-online-bg, var(--surface-ground));
                color: var(--primary-color);
            }
            .lgeo__icon--empty {
                background: var(--surface-ground);
                color: var(--text-color-secondary);
            }
            .lgeo__body {
                flex: 1;
                min-width: 12rem;
                display: flex;
                flex-direction: column;
            }
            .lgeo__value {
                font-weight: 600;
                overflow-wrap: anywhere;
            }
            .lgeo__source,
            .lgeo__muted {
                font-size: 0.8125rem;
                color: var(--text-color-secondary);
            }
            .lgeo__maps {
                font-size: 0.8125rem;
                white-space: nowrap;
                color: var(--primary-color);
                text-decoration: none;
                i {
                    font-size: 0.7rem;
                }
            }
            .lgeo__maps:hover {
                text-decoration: underline;
            }
            .lgeo__edit {
                align-self: flex-start;
            }
        `
    ]
})
export class LocationGeoComponent {
    private readonly api = inject(CloudApiService);
    private readonly messages = inject(MessageService);
    readonly t = inject(TranslationService).t;

    readonly locationId = input.required<string>();
    readonly locationName = input('');
    readonly canManage = input(false);

    readonly data = signal<LocationGeoResponse | null>(null);
    readonly loadFailed = signal(false);
    readonly dialogVisible = signal(false);
    private loads = 0;

    constructor() {
        effect(() => {
            const id = this.locationId();
            untracked(() => void this.load(id));
        });
    }

    place(geo: ResolvedGeo): string {
        return geo.address?.trim() || formatCoordinates(geo.latitude, geo.longitude);
    }

    mapsUrl(geo: ResolvedGeo): string {
        return googleMapsUrl(geo.latitude, geo.longitude);
    }

    sourceText(geo: ResolvedGeo): string {
        if (geo.sourceLocationId === this.locationId()) return this.t()('locationGeo.setHere');
        return this.t()('locationGeo.fromLocation').replace('{name}', geo.sourceLocationName || '');
    }

    changed(response: LocationGeoResponse): void {
        const wasSet = !!this.data()?.override;
        // The open tree picks the new position up from the locations:updated event.
        this.data.set(response);
        this.messages.add({ severity: 'success', summary: this.t()(response.override || !wasSet ? 'locationGeo.saved' : 'locationGeo.cleared') });
    }

    private async load(id: string): Promise<void> {
        const request = ++this.loads;
        this.data.set(null);
        this.loadFailed.set(false);
        try {
            const data = await this.api.getLocationGeo(id);
            if (request === this.loads) this.data.set(data);
        } catch {
            if (request === this.loads) this.loadFailed.set(true);
        }
    }
}
